import { formatChildAge, scrollToTop, twClassMerge } from '@healthhub/react-ui';
import { Divider, Skeleton } from 'antd';
import type { ReactNode } from 'react';
import { memo, useMemo, useState } from 'react';

import { OutlinedDownArrowIcon, OutlinedUpArrowIcon } from '@shared/assets-icon';
import { Button } from '@shared/components/Button';
import type { SwitchProfileTypeType } from '@shared/constants/constants';
import { SwitchProfileType } from '@shared/constants/constants';
import { useCurrentUserOrCurrentFederatedUser } from '@shared/hooks/useCurrentUserOrCurrentFederatedUser';
import useMemberIdentifier from '@shared/hooks/useMemberIdentifier';
import { useGetProfilesDetailsQuery } from '@shared/services/userProfile';
import { useAppSelector } from '@shared/stores/rootTypes';
import { selectedProfileSelector } from '@shared/stores/user/userSelector';
import { getDisplayName } from '@shared/utils/displayName';

import type { InformationHeaderContentProps } from './InformationHeaderContent';
import InformationHeaderContent from './InformationHeaderContent';
import SwitchProfileDropdown from '../SwitchProfileDropdown/SwitchProfileDropdown';

interface Props {
  allowCurrentUserNavigation?: boolean;
  bypassEmptyCheckAndShowSelf?: boolean;
  className?: string;
  customSwitchUser?: ReactNode;
  customSwitchUserNavigatePath?: string;
  customSwitchUserReplaceHistory?: boolean;
  expandSwitchUser?: boolean;
  hideAge?: boolean;
  hideRelationship?: boolean;
  isShowSwitchUser?: boolean;
  onClickSwitchUser?: (toggleExpand: () => void) => void;
  onCloseSwitchUser?: () => void;
  pageLevel?: number;
  showChildrenAge?: boolean;
  showSelfInSwitchProfile?: boolean;
  switchProfileType?: SwitchProfileTypeType;
}

const InformationHeader = ({
  allowCurrentUserNavigation = false,
  bypassEmptyCheckAndShowSelf,
  className,
  customSwitchUser,
  customSwitchUserNavigatePath,
  customSwitchUserReplaceHistory = true,
  expandSwitchUser = false,
  hideAge,
  hideRelationship,
  isShowSwitchUser = false,
  onClickSwitchUser,
  onCloseSwitchUser,
  pageLevel,
  showChildrenAge,
  showSelfInSwitchProfile,
  switchProfileType = SwitchProfileType.ALL,
}: Props) => {
  const selectedUser = useAppSelector(selectedProfileSelector);
  const [isExpandSwitchUser, setIsExpandSwitchUser] = useState(expandSwitchUser);
  const {
    userProfile,
    isLoading: isLoadingUserProfile,
    error: userProfileError,
  } = useCurrentUserOrCurrentFederatedUser({
    isPatientMasterInfoRequired: true,
  });

  const { isFetchingUserFamily, memberIdentifier } = useMemberIdentifier();

  const { data: childDetails, isFetching: isLoadingChildDetails } = useGetProfilesDetailsQuery(
    {
      MemberPrn: memberIdentifier,
    },
    {
      skip: !showChildrenAge || !memberIdentifier,
    }
  );

  const onToggleExpandSwitchUser = () => {
    setIsExpandSwitchUser((prev) => !prev);
  };

  const onClickSwitchUserButton = () => {
    if (onClickSwitchUser) {
      onClickSwitchUser(onToggleExpandSwitchUser);
      return;
    }
    setIsExpandSwitchUser((prev) => !prev);
  };

  const onCloseSwitchProfileDropdown = () => {
    setIsExpandSwitchUser(false);
    onCloseSwitchUser?.();
    setTimeout(() => {
      scrollToTop();
    }, 0);
  };

  const headerContentProps: InformationHeaderContentProps = useMemo(
    () =>
      !selectedUser?.memberIdentifier
        ? {
            age: undefined,
            gender: undefined,
            hideAge,
            hideRelationship,
            name: getDisplayName(userProfile),
            nric: userProfile?.MaskedNric ?? '',
            photo: userProfile?.Photo?.Data ?? '',
          }
        : {
            age: showChildrenAge ? childDetails?.Details?.Months : selectedUser?.age,
            displayAge: showChildrenAge
              ? formatChildAge(childDetails?.Details?.Months)
              : (selectedUser?.displayAge ?? ''),
            gender: undefined,
            hideAge,
            hideRelationship,
            name: selectedUser?.name ?? '',
            nric: selectedUser?.nric ?? '',
            photo: selectedUser?.photo,
            relationship: selectedUser?.relationship ?? '',
          },
    [
      childDetails,
      hideAge,
      hideRelationship,
      selectedUser?.age,
      selectedUser?.displayAge,
      selectedUser?.memberIdentifier,
      selectedUser?.name,
      selectedUser?.nric,
      selectedUser?.photo,
      selectedUser?.relationship,
      showChildrenAge,
      userProfile,
    ]
  );

  if ((isLoadingUserProfile && !userProfileError) || isFetchingUserFamily || isLoadingChildDetails) {
    return (
      <Skeleton
        active
        avatar
        className="bg-white p-3"
        paragraph={{
          rows: 1,
        }}
      />
    );
  }

  return (
    <div className="relative">
      {!!isExpandSwitchUser && <div className="fixed inset-0 z-overlay-20 bg-black opacity-50" />}
      <div
        className={twClassMerge('relative', {
          'z-overlay-20': isExpandSwitchUser,
        })}
      >
        <div className="flex h-full flex-row items-center justify-between bg-white" lang="en">
          <InformationHeaderContent {...headerContentProps} className={className} />
          {!!isShowSwitchUser && (
            <Button className="mr-2 flex flex-row px-2 font-normal" onClick={onClickSwitchUserButton} variant="text">
              <span className="mt-0.5">{isExpandSwitchUser ? 'Close' : 'Switch'}</span>
              {isExpandSwitchUser ? (
                <OutlinedUpArrowIcon className="size-6" />
              ) : (
                <OutlinedDownArrowIcon className="size-6" />
              )}
            </Button>
          )}
          {customSwitchUser}
        </div>

        {!!isExpandSwitchUser && (
          <>
            <div className="bg-white px-4">
              <Divider className="m-0 border border-functional-stone" />
            </div>
            <div className="absolute z-dropdown w-full">
              <SwitchProfileDropdown
                allowCurrentUserNavigation={allowCurrentUserNavigation}
                bypassEmptyCheckAndShowSelf={bypassEmptyCheckAndShowSelf}
                customSwitchUserNavigatePath={customSwitchUserNavigatePath}
                customSwitchUserReplaceHistory={customSwitchUserReplaceHistory}
                onCloseDropdown={onCloseSwitchProfileDropdown}
                pageLevel={pageLevel}
                showSelfInSwitchProfile={showSelfInSwitchProfile}
                switchProfileType={switchProfileType}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(InformationHeader);
